import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserMd,
  faClinicMedical,
  faSave,
  faImage,
  faMapMarkerAlt,
  faPhone,
  faClock,
  faStethoscope,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./DoctorPortal.module.css";

const DoctorProfile = ({ doctor = {}, onSaveAll }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    specialty: "",
    location: "",
    workingHours: "",
    contactNumber: "",
    profilePicture: "",
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (doctor && Object.keys(doctor).length > 0) {
      setFormData({
        firstName: doctor.firstName || "",
        lastName: doctor.lastName || "",
        specialty: doctor.specialty || "",
        location: doctor.location || "",
        workingHours: doctor.workingHours || "",
        contactNumber: doctor.contactNumber || doctor.phone || "",
        profilePicture: doctor.profilePicture || doctor.image || "",
      });
    }
  }, [doctor]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleBatchSave = async () => {
    if (onSaveAll) {
      setSaving(true);
      try {
        await onSaveAll(formData);
      } finally {
        setSaving(false);
      }
    }
  };

  return (
    <div>
      {/* View Header */}
      <div className={styles.viewHeader}>
        <div>
          <h1 className={styles.viewTitle}>Profile & Clinic Settings</h1>
          <p className={styles.viewSubtitle}>
            Manage clinical credentials, practice location, and public contact information
          </p>
        </div>
      </div>

      <div className={styles.profileGrid}>
        {/* Card 1: Identity */}
        <div className={styles.glassCard}>
          <h3 style={{ fontSize: "1.25rem", fontWeight: 800, color: "#0f172a", margin: "0 0 1.5rem 0", display: "flex", alignItems: "center", gap: "10px", borderBottom: "1px solid #f1f5f9", paddingBottom: "1rem" }}>
            <FontAwesomeIcon icon={faUserMd} style={{ color: "#2d6a4f" }} />
            <span>Identity & Credentials</span>
          </h3>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "1.5rem" }}>
            <div>
              <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 750, color: "#475569", marginBottom: "6px" }}>
                First Name
              </label>
              <input
                type="text"
                placeholder="e.g. Robert"
                value={formData.firstName}
                onChange={(e) => handleChange("firstName", e.target.value)}
                className={styles.inputField}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 750, color: "#475569", marginBottom: "6px" }}>
                Last Name
              </label>
              <input
                type="text"
                placeholder="e.g. Wilson"
                value={formData.lastName}
                onChange={(e) => handleChange("lastName", e.target.value)}
                className={styles.inputField}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 750, color: "#475569", marginBottom: "6px" }}>
                Clinical Specialty
              </label>
              <div style={{ position: "relative" }}>
                <FontAwesomeIcon icon={faStethoscope} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
                <input
                  type="text"
                  placeholder="e.g. Senior Orthopedic Specialist"
                  value={formData.specialty}
                  onChange={(e) => handleChange("specialty", e.target.value)}
                  className={styles.inputField}
                  style={{ paddingLeft: "2.25rem" }}
                />
              </div>
            </div>

            <div>
              <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 750, color: "#475569", marginBottom: "6px" }}>
                Profile Photo URL
              </label>
              <div style={{ position: "relative" }}>
                <FontAwesomeIcon icon={faImage} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
                <input
                  type="text"
                  placeholder="https://images.unsplash.com/..."
                  value={formData.profilePicture}
                  onChange={(e) => handleChange("profilePicture", e.target.value)}
                  className={styles.inputField}
                  style={{ paddingLeft: "2.25rem" }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Clinic Details */}
        <div className={styles.glassCard}>
          <h3 style={{ fontSize: "1.25rem", fontWeight: 800, color: "#0f172a", margin: "0 0 1.5rem 0", display: "flex", alignItems: "center", gap: "10px", borderBottom: "1px solid #f1f5f9", paddingBottom: "1rem" }}>
            <FontAwesomeIcon icon={faClinicMedical} style={{ color: "#2d6a4f" }} />
            <span>Clinic Details & Operations</span>
          </h3>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "1.5rem" }}>
            <div>
              <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 750, color: "#475569", marginBottom: "6px" }}>
                Practice Location / Address
              </label>
              <div style={{ position: "relative" }}>
                <FontAwesomeIcon icon={faMapMarkerAlt} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
                <input
                  type="text"
                  placeholder="e.g. Suite 402, ReformMe Medical Center, London"
                  value={formData.location}
                  onChange={(e) => handleChange("location", e.target.value)}
                  className={styles.inputField}
                  style={{ paddingLeft: "2.25rem" }}
                />
              </div>
            </div>

            <div>
              <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 750, color: "#475569", marginBottom: "6px" }}>
                Contact Phone Number
              </label>
              <div style={{ position: "relative" }}>
                <FontAwesomeIcon icon={faPhone} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
                <input
                  type="text"
                  placeholder="e.g. +44 20 7946 0921"
                  value={formData.contactNumber}
                  onChange={(e) => handleChange("contactNumber", e.target.value)}
                  className={styles.inputField}
                  style={{ paddingLeft: "2.25rem" }}
                />
              </div>
            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 750, color: "#475569", marginBottom: "6px" }}>
                Working Hours / Schedule Note
              </label>
              <div style={{ position: "relative" }}>
                <FontAwesomeIcon icon={faClock} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
                <input
                  type="text"
                  placeholder="e.g. Mon - Fri: 09:00 AM - 05:00 PM (Sat by appointment)"
                  value={formData.workingHours}
                  onChange={(e) => handleChange("workingHours", e.target.value)}
                  className={styles.inputField}
                  style={{ paddingLeft: "2.25rem" }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Save All Changes Button anchored to bottom right */}
      <div className={styles.stickySaveBar}>
        <button
          type="button"
          onClick={handleBatchSave}
          disabled={saving}
          className={styles.primaryBtn}
          style={{
            padding: "1rem 2rem",
            fontSize: "1.05rem",
            borderRadius: "9999px",
            boxShadow: "0 10px 30px rgba(45, 106, 79, 0.4)",
            border: "2px solid rgba(255, 255, 255, 0.3)",
          }}
        >
          <FontAwesomeIcon icon={faSave} />
          <span>{saving ? "Saving Changes..." : "Save All Changes"}</span>
        </button>
      </div>
    </div>
  );
};

export default DoctorProfile;
